import { useState } from 'react';
import { MdChevronLeft } from 'react-icons/md';
import CaseReviewModal from '../CaseReviewModal';
import type { CaseFormData, FeeBreakdown } from '../types';
import WalkInForm from './components/WalkInForm';
import CourtReferralForm from './components/CourtReferralForm';

export interface CreateCaseFormProps {
    /** 'walk-in' | 'court-referral' | 'online' */
    caseType: string;
    title?: string;
    subtitle?: string;
    onBack?: () => void;
    showTerms?: boolean;
    onSubmit: (caseType: string, formData: CaseFormData) => Promise<void>;
    onSuccess?: () => void;
    fees?: FeeBreakdown;
    /** Hide the Cash / POS / Transfer radio in the payment step — used when
     *  payment is handled off-site via a gateway redirect. */
    hidePaymentMethod?: boolean;
}

export default function CreateCaseForm({
    caseType,
    title = 'Create New Case',
    subtitle,
    onBack,
    showTerms,
    onSubmit,
    onSuccess,
    fees,
    hidePaymentMethod,
}: CreateCaseFormProps) {
    const [reviewOpen, setReviewOpen] = useState(false);
    const [formData, setFormData] = useState<CaseFormData>({
        disputeCategory: 'Civil',
        description: '',
        files: [],
        applicantType: 'Individual',
        applicants: [{ name: '', address: '', phone: '', email: '' }],
        respondentType: 'Individual',
        respondents: [{ name: '', address: '', phone: '', email: '' }],
        defendant: { name: '', address: '', phone: '', email: '' },
        claimant: { name: '', address: '', phone: '', email: '' },
        uploadedDocs: new Set(),
    });

    const updateFormData = (update: Partial<CaseFormData>) => setFormData((prev) => ({ ...prev, ...update }));

    return (
        <div>
            <div className="mb-2 flex items-center gap-3">
                {onBack && (
                    <button
                        type="button"
                        title="Back"
                        onClick={onBack}
                        className="flex h-9 w-9 items-center justify-center rounded-full border border-[#eee] text-[#555] transition-colors hover:bg-[#f5f5f5]"
                    >
                        <MdChevronLeft size={20} />
                    </button>
                )}
                <div>
                    <h1 className="text-2xl font-bold text-secondary sm:text-3xl">{title}</h1>
                    {subtitle && <p className="mt-1 text-[13px] text-[#555]">{subtitle}</p>}
                </div>
            </div>

            <div className="mt-8">
                {caseType === 'court-referral' ? (
                    <CourtReferralForm data={formData} onUpdate={updateFormData} onSubmit={() => setReviewOpen(true)} />
                ) : (
                    <WalkInForm data={formData} onUpdate={updateFormData} onSubmit={() => setReviewOpen(true)} showTerms={showTerms} />
                )}
            </div>

            <CaseReviewModal
                open={reviewOpen}
                onClose={() => setReviewOpen(false)}
                caseType={caseType}
                formData={formData}
                fees={fees}
                hidePaymentMethod={hidePaymentMethod}
                onUpdate={updateFormData}
                onSubmit={onSubmit}
                onSuccess={onSuccess}
            />
        </div>
    );
}
